import { Injectable } from '@angular/core';
import {
  CreateImportInput,
  Import,
  ImportListParams,
  ImportOrderByInputEnum,
  PagedResult,
  StartImportAndValidationInput,
  UpdateImportFileInput,
  UploadCredential,
  UploadCredentialEntityEnum,
  UploadCredentialInput
} from '@core/interfaces';
import { Apollo, QueryRef } from 'apollo-angular';
import { ImportsQueriesService } from './queries/imports.queries.service';

@Injectable({
  providedIn: 'root'
})
export class ImportsService {
  constructor(
    private readonly apollo: Apollo,
    private queries: ImportsQueriesService
  ) {}

  public async listImports({ where, options }: ImportListParams): Promise<PagedResult<Import>> {
    const query = this.queries.listImports();
    const variables = {
      orderBy: ImportOrderByInputEnum[options.orderBy],
      where: where,
      limit: options.limit,
      offset: options.offset
    };

    return this.apollo
      .query<PagedResult<Import>>({ query, variables })
      .toPromise()
      .then((response) => response.data['imports']);
  }

  public async getImportById(id: string): Promise<Import> {
    const query = this.queries.getImportById();
    const variables = { id };

    return this.apollo
      .query<Import>({ query, variables })
      .toPromise()
      .then((response) => response.data['import']);
  }

  public watchImportById(id: string): QueryRef<any, any> {
    const query = this.queries.getImportById();
    const variables = { id };
    return this.apollo
      .watchQuery({ query, variables, pollInterval: 2000 });
  }

  public async createUploadCredential({ size, extension, entity }: UploadCredentialInput): Promise<UploadCredential> {
    const mutation = this.queries.generateUploadCredential();
    const variables = {
      input: {
        size: size,
        extension: extension,
        entity: entity
      }
    };

    return this.apollo
      .mutate<UploadCredential>({ mutation, variables })
      .toPromise()
      .then((response) => response.data['generateUploadCredential']);
  }

  public async createImport({ type, fileKey, bucketName }: CreateImportInput): Promise<Import> {
    const mutation = this.queries.createImport();
    const variables = {
      input: {
        type,
        fileKey,
        bucketName
      }
    };

    return this.apollo
      .mutate<Import>({ mutation, variables })
      .toPromise()
      .then((response) => response.data['createImport']);
  }

  public async updateImportFile({ id, updatedAt, fileKey, bucketName }: UpdateImportFileInput): Promise<Import> {
    const mutation = this.queries.updateImportFile();
    const variables = {
      input: {
        id,
        fileKey,
        updatedAt,
        bucketName
      }
    };

    return this.apollo
      .mutate<Import>({ mutation, variables })
      .toPromise()
      .then((response) => response.data['updateImportFile']);
  }

  public async startImportValidation({ id, updatedAt, language }: StartImportAndValidationInput): Promise<Import> {
    const mutation = this.queries.startImportValidation();
    const variables = {
      input: {
        id,
        updatedAt,
        language
      }
    };

    return this.apollo
      .mutate<Import>({ mutation, variables })
      .toPromise()
      .then((response) => response.data['startImportValidation']);
  }

  public async startImport({ id, updatedAt, language }: StartImportAndValidationInput): Promise<Import> {
    const mutation = this.queries.startImport();
    const variables = {
      input: {
        id,
        updatedAt,
        language
      }
    };

    return this.apollo
      .mutate<Import>({ mutation, variables })
      .toPromise()
      .then((response) => response.data['startImport']);
  }
}
